/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { useCartSWRKey } from '@/data/Content/Cart';
import { setDefaultRequest } from '@/data/Content/DefaultStore';
import { useFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature';
import { useNotifications } from '@/data/Content/Notifications';
import { usePrivacyAndMarketing } from '@/data/Content/PrivacyAndMarketing';
import { useSessionDialog } from '@/data/Content/SessionDialog';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getLocalization } from '@/data/Localization';
import { dFix, useSettings } from '@/data/Settings';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { NOT_APPLICABLE } from '@/data/constants/gtm';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { CurrentUser, saveCurrentUserToStorage } from '@/data/forReactAndMobile/SaveCurrentUser';
import { WC_TOKEN, WC_TRUSTED_TOKEN } from '@/data/forReactAndMobile/constants';
import { useRememberMeState } from '@/data/state/useRememberMeState';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import type { ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationAdminAddRequest } from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import transactionsPerson from 'integration/generated/transactions/transactionsPerson';
import { isUndefined } from 'lodash';
import { GA4_AUTH_EVENTS } from 'presentation/events/constants/eventConstants';
import { useTrackAuthEvent } from 'presentation/events/hooks/useTrackAuthEvent';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { GA4CreateAccountCompletePayload } from 'presentation/events/types/ga4Types';
import { useMemo, useState } from 'react';
import { Cookies } from 'react-cookie';
import {
	DEFAULT_STORE_ID,
	GEO_LOCATION_COOKIE_NAME,
	MIN_EXP_TIME,
} from 'shared/custom/constants/commonConstants';
import { useSWRConfig } from 'swr';

type FirstStepResponse = {
	[key: string]: any;
};

type UserRegistration = {
	logonId?: string; // optional for backward compatibility
	/**
	 * @deprecated do not use.
	 */
	email?: string;
	verifyEmail: string;
	phone1?: string;
	firstName: string;
	lastName: string;
	logonPassword: string;
	logonPasswordVerify: string;
	receiveEmail: boolean;
	rememberMe: boolean;
	marketingTrackingConsent?: boolean;
	verificationToken?: string;
	storeId?: string;
	catalogId?: string;
	isDialogView?: boolean;
};

type RegistrationPayload =
	ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationAdminAddRequest & {
		storeId?: string;
		catalogId?: string;
		receiveEmailPreference?: { value: string; storeId: string }[];
		marketingTrackingConsent?: string;
		privacyNoticeVersion?: string;
	};

const initialRegistrationValue: UserRegistration = {
	logonId: '',
	email: '',
	verifyEmail: '',
	firstName: '',
	lastName: '',
	logonPassword: '',
	logonPasswordVerify: '',
	phone1: '',
	receiveEmail: false,
	rememberMe: false,
	verificationToken: '',
};

const registrationFetcher =
	(pub: boolean, isFirstStep: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: RegistrationPayload,
		params: RequestParams
	) => {
		if (isFirstStep) {
			return await transactionsPerson(pub).personFirstStepRegistration(storeId, data);
		} else {
			return await transactionsPerson(pub).personRegisterPersonOnUserRegistrationAdminAdd(
				storeId,
				query,
				data,
				params
			);
		}
	};

const mapRegistrationToPayload = (
	props: UserRegistration,
	privacyNoticeVersion: string | number | undefined,
	includeVerificationToken: boolean,
	preferredLanguage: string,
	storeId: string,
	catalogId: string
): RegistrationPayload => {
	const {
		receiveEmail,
		email,
		logonId,
		verificationToken,
		marketingTrackingConsent: _consent,
		verifyEmail,
		...rest
	} = props;

	const requestData = <ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationAdminAddRequest>{
		...rest,
		logonId: ((logonId || email) ?? '').toLowerCase(),
		email1: email?.toLowerCase(),
		verifyEmail: verifyEmail?.toLowerCase(),
		receiveEmail: receiveEmail.toString(),
		preferredLanguage,
		profileType: 'C',
		registerType: 'G',
		challengeQuestion: '-',
		challengeAnswer: '-',
		mobilePhone1: includeVerificationToken ? verificationToken : undefined,
	};

	const externalData = {
		storeId,
		catalogId,
		receiveEmailPreference: [{ value: receiveEmail.toString(), storeId }],
	};

	const privacyData: { marketingTrackingConsent?: string; privacyNoticeVersion?: string } = {};
	if (!isUndefined(_consent)) {
		privacyData.marketingTrackingConsent = _consent ? '1' : '0';
	}
	if (!isUndefined(privacyNoticeVersion)) {
		privacyData.privacyNoticeVersion = String(privacyNoticeVersion);
	}

	return { ...requestData, ...externalData, ...privacyData };
};

export const getRegistration = async ({ cache, context }: ContentProps) => {
	const { localeName: locale } = await getStoreLocale({ cache, context });
	await getLocalization(cache, locale, 'RegistrationLayout');
};

export const useRegistration = () => {
	const [showVerificationTokenForm, setShowVerificationTokenForm] = useState<boolean>(false);
	const [firstStep, setFirstStep] = useState<boolean>(true);
	const [isTokenInvalid, setIsTokenInvalid] = useState<boolean>(false);
	const { settings } = useSettings();
	const { mutate } = useSWRConfig();
	const router = useNextRouter();
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const trackAuthEvent = useTrackAuthEvent();
	const trackException = useTrackException();

	const {
		actions: { setRememberMe },
	} = useRememberMeState();
	const { langId: preferredLanguage } = getClientSideCommon(settings, router);
	const { data: _consentFeature } = useFlexFlowStoreFeature({
		id: EMS_STORE_FEATURE.CONSENT_OPTIONS,
	});
	const { featureEnabled: consentFeature } = _consentFeature;
	const storeId = settings?.storeId ?? '';
	const catalogId = settings?.defaultCatalogId ?? '';
	const { closeSessionDialog } = useSessionDialog();
	const { privacyNoticeVersion, setMarketingTrackingConsent, setPrivacyNoticeVersion } =
		usePrivacyAndMarketing();
	const currentCartSWRKey = useCartSWRKey();

	const initialRegistration = useMemo(
		() =>
			consentFeature
				? { ...initialRegistrationValue, marketingTrackingConsent: false }
				: { ...initialRegistrationValue },
		[consentFeature]
	);

	const resendCode = async (props: UserRegistration) => {
		setIsTokenInvalid(false);

		try {
			const data = mapRegistrationToPayload(
				props,
				privacyNoticeVersion,
				false,
				preferredLanguage,
				storeId,
				catalogId
			);

			await registrationFetcher(true, true)(
				storeId,
				{ rememberMe: props.rememberMe, updateCookies: 'true' },
				data,
				params
			);
		} catch (e) {
			const err = e as TransactionErrorResponse;
			trackException(e, props.isDialogView);
			console.error('Error during resendCode:', err);
		}
	};
	// eslint-disable-next-line complexity
	const registrationSubmit = async (props: UserRegistration) => {
		const data = mapRegistrationToPayload(
			props,
			privacyNoticeVersion,
			showVerificationTokenForm,
			preferredLanguage,
			storeId,
			catalogId
		);

		const commonQuery = { rememberMe: props.rememberMe, updateCookies: 'true' };

		const createAccountCompleteParams: GA4CreateAccountCompletePayload = {
			event: GA4_AUTH_EVENTS.CREATE_ACCOUNT_COMPLETE,
			element_name: 'registration form',
			element_type: props.isDialogView ? 'dialog' : 'page',
			element_location: props.isDialogView ? 'registration dialog' : 'registration page',
			page: window.location.href,
			error_type: NOT_APPLICABLE,
			status: 'success',
		};

		try {
			const response = (await registrationFetcher(true, firstStep)(
				storeId,
				commonQuery,
				data,
				params
			)) as FirstStepResponse;
			const { sendEmail } = response?.response || {};
			// Save user session data in localstorage
			if (response && !firstStep) {
				const cookies = new Cookies();
				const currentUser = {
					...response,
					userLoggedIn: true,
					isGuest: false,
					generatedCookie: true,
				} as CurrentUser;

				saveCurrentUserToStorage(
					process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '',
					currentUser
				);
				const cookieValue = cookies.get(GEO_LOCATION_COOKIE_NAME) ?? 'defaultStore';
				const defaultStorePayload = {
					defaultStore: cookieValue?.stLocId ?? DEFAULT_STORE_ID,
					fromPageName: '2',
				};
				await setDefaultRequest(defaultStorePayload, storeId, preferredLanguage, MIN_EXP_TIME, {
					headers: {
						[WC_TOKEN]: response.WCToken,
						[WC_TRUSTED_TOKEN]: response.WCTrustedToken,
					},
				});
			}
			if (sendEmail) {
				setShowVerificationTokenForm(true);
				setFirstStep(false); // Next submission will be the token verification step
				return false;
			}

			setRememberMe(props.rememberMe);
			const consent = data.marketingTrackingConsent;
			const privacyVersion = data.privacyNoticeVersion;

			if (privacyVersion) setPrivacyNoticeVersion(dFix(privacyVersion));
			if (consent) setMarketingTrackingConsent(dFix(consent));

			if (showVerificationTokenForm || !firstStep) {
				trackAuthEvent(createAccountCompleteParams);
			}

			await mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
			await mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
			await mutate(cartMutatorKeyMatcher('')); // at current page
			await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined); // all cart except current cart, e.g different locale
			closeSessionDialog();
			return true;
		} catch (e) {
			const err = e as TransactionErrorResponse;
			const errorParameters = err?.error?.errors?.[0]?.errorParameters;
			const isTokenInvalidError = errorParameters === 'TOKEN_INVALID';
			if (isTokenInvalidError) {
				setIsTokenInvalid(true);
			} else {
				notifyError(processError(err));
				trackAuthEvent({
					...createAccountCompleteParams,
					status: 'error',
					error_type: err.error.errors[0]?.errorCode || 'error',
				});
			}

			trackException(e, props.isDialogView);
			return false;
		}
	};

	return {
		registrationSubmit,
		initialRegistration,
		showVerificationTokenForm,
		resendCode,
		isTokenInvalid,
	};
};
